/**
 * expenseBooking — reine Buchungslogik für Ausgaben (Eingangsbelege), SKR04.
 *
 *   Aufwand (netto, je Steuersatz)   Soll
 *   Vorsteuer 1406 (19 %) / 1401 (7 %) Soll
 *     an Zahlungskonto / 3300 Kreditor  Haben (brutto)
 *
 * Bewirtung: Netto 70 % auf 6640, 30 % auf 6644; Vorsteuer voll abziehbar.
 * Keine Seiteneffekte — die Buchung selbst macht lib/ausgabenApi.js über
 * fibu_post_entry (Soll = Haben wird dort zusätzlich geprüft).
 */

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const fmt = (n) => round2(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const ZAHLUNGSARTEN = {
  bank: { label: 'Bank', account: '1800' },
  kasse: { label: 'Kasse (bar)', account: '1600' },
  paypal: { label: 'PayPal', account: '1810' },
  privat: { label: 'Privat ausgelegt', account: '3500' },
  offen: { label: 'Offen (Rechnung)', account: '3300' },
};

const INPUT_TAX = {
  19: { code: 'VSt19', account: '1406' },
  7: { code: 'VSt7', account: '1401' },
};

export const BEWIRTUNG_NICHT_ABZUGSFAEHIG = '6644';

export function creditAccountFor(zahlungsart) {
  const z = ZAHLUNGSARTEN[zahlungsart];
  if (!z) throw new Error(`Unbekannte Zahlungsart: ${zahlungsart}`);
  return z.account;
}

/** Brutto → {tax_rate, netto, steuer}; netto + steuer = brutto (auf den Cent) */
export function splitGross(brutto, taxRate) {
  const rate = Number(taxRate) || 0;
  const gross = round2(brutto);
  const netto = round2(gross / (1 + rate / 100));
  return { tax_rate: rate, netto, steuer: round2(gross - netto) };
}

export function normalizePositionen(positionen) {
  return (Array.isArray(positionen) ? positionen : [])
    .map((p) => {
      const rate = Number(p?.tax_rate) || 0;
      const netto = round2(p?.netto);
      const steuer = p?.steuer === undefined || p?.steuer === null || p?.steuer === ''
        ? round2(netto * rate / 100)
        : round2(p.steuer);
      const row = { tax_rate: rate, netto, steuer };
      if (p?.category_id) row.category_id = Number(p.category_id);
      if (p?.beschreibung) row.beschreibung = String(p.beschreibung);
      return row;
    })
    .filter((p) => p.netto !== 0 || p.steuer !== 0);
}

export function sumPositionen(positionen) {
  const rows = normalizePositionen(positionen);
  const netto = round2(rows.reduce((s, p) => s + p.netto, 0));
  const steuer = round2(rows.reduce((s, p) => s + p.steuer, 0));
  return { netto, steuer, brutto: round2(netto + steuer) };
}

/** Pflichtfelder vor dem Buchen; liefert deutsche Fehlermeldungen */
export function validateExpense(expense) {
  const errors = [];
  if (!expense?.beleg_datum) errors.push('Belegdatum fehlt');
  if (!String(expense?.lieferant_name || '').trim()) errors.push('Lieferant fehlt');
  const rows = Array.isArray(expense?.positionen) ? expense.positionen : [];
  const allRowsHaveCategory = rows.length > 0 && rows.every((p) => p?.category_id);
  if (!expense?.category_id && !allRowsHaveCategory) errors.push('Kategorie fehlt');
  const brutto = round2(expense?.brutto);
  if (!(brutto > 0)) errors.push('Bruttobetrag fehlt');
  const sum = sumPositionen(expense?.positionen);
  if (brutto > 0 && sum.brutto > 0 && Math.abs(sum.brutto - brutto) > 0.005) {
    errors.push(`Summe der Positionen (${fmt(sum.brutto)} €) ≠ Brutto (${fmt(brutto)} €)`);
  }
  if (brutto > 0 && sum.brutto === 0) errors.push('Steuerpositionen fehlen');
  if (expense?.zahlungsart === 'offen' && !expense?.faellig_am) errors.push('Fälligkeitsdatum fehlt');
  return errors;
}

function expenseAccountFor(category, rate, debitAccountNo) {
  if (debitAccountNo) return debitAccountNo;
  if (rate === 7 && category?.account_no_7) return category.account_no_7;
  return category?.account_no || null;
}

function debitLinesForPosition(p, category, debitAccountNo, baseText) {
  const text = p.beschreibung ? `${baseText} · ${p.beschreibung}` : baseText;
  const account = expenseAccountFor(category, p.tax_rate, debitAccountNo);
  if (!account) throw new Error('Für diese Kategorie ist kein Aufwandskonto hinterlegt');
  const tax = INPUT_TAX[p.tax_rate];
  const withCode = (line) => (tax ? { ...line, tax_code: tax.code } : line);

  const lines = [];
  if (category?.split_rule === 'bewirtung_70_30' && !debitAccountNo) {
    const abzugsfaehig = round2(p.netto * 0.7);
    lines.push(withCode({ account_no: account, debit: abzugsfaehig, credit: 0, description: `${text} (70 %)` }));
    lines.push({ account_no: BEWIRTUNG_NICHT_ABZUGSFAEHIG, debit: round2(p.netto - abzugsfaehig), credit: 0, description: `${text} (30 % n. abz.)` });
  } else {
    lines.push(withCode({ account_no: account, debit: p.netto, credit: 0, description: text }));
  }
  if (p.steuer > 0) {
    if (!tax) throw new Error(`Steuersatz ${p.tax_rate} % wird nicht unterstützt (nur 0, 7, 19)`);
    lines.push({ account_no: tax.account, debit: p.steuer, credit: 0, description: `Vorsteuer ${p.tax_rate} %` });
  }
  return lines;
}

/**
 * Buchungszeilen für fibu_post_entry.
 * @param {{ expense: object, category: object, categoriesById?: Map, debitAccountNo?: string }} args
 *   category: Kategorie der Ausgabe (Fallback für Positionen ohne eigene Kategorie)
 *   categoriesById: alle Kategorien, wenn Positionen eigene category_id tragen
 *   debitAccountNo überschreibt das Aufwandskonto (z. B. Anlagekonto).
 */
export function buildExpenseLines({ expense, category, categoriesById = null, debitAccountNo = null }) {
  const brutto = round2(expense?.brutto);
  if (!(brutto > 0)) throw new Error('Bruttobetrag fehlt');
  const positionen = normalizePositionen(expense.positionen);
  if (positionen.length === 0) throw new Error('Steuerpositionen fehlen');
  const sum = sumPositionen(positionen);
  if (Math.abs(sum.brutto - brutto) > 0.005) {
    throw new Error(`Summe der Positionen (${fmt(sum.brutto)} €) ≠ Brutto (${fmt(brutto)} €)`);
  }

  const text = [expense.lieferant_name, expense.beleg_nr].filter(Boolean).join(' ') || 'Ausgabe';
  const categoryOf = (p) => (p.category_id && categoriesById?.get(Number(p.category_id))) || category;
  const debitLines = positionen.flatMap((p) => debitLinesForPosition(p, categoryOf(p), debitAccountNo, text));
  const creditLine = {
    account_no: creditAccountFor(expense.zahlungsart),
    debit: 0,
    credit: brutto,
    description: `${ZAHLUNGSARTEN[expense.zahlungsart].label}: ${text}`,
  };

  // Rundungsdifferenz (Cent) auf die erste Aufwandszeile
  const debitSum = round2(debitLines.reduce((s, l) => s + l.debit, 0));
  const diff = round2(brutto - debitSum);
  const adjusted = diff === 0
    ? debitLines
    : debitLines.map((l, i) => (i === 0 ? { ...l, debit: round2(l.debit + diff) } : l));
  return [...adjusted, creditLine];
}
